import type { CSSProperties, MouseEvent, ReactNode } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import clsx from 'clsx'

/**
 * Anything inside a card that owns its own click. A card that navigates must not steal these.
 *
 * `label` is here because the Reminders composer's field sits inside one, and a click on the
 * label is a click on the field. `[data-keeps-click]` is the escape hatch for a control that is
 * none of the above — a custom row that handles pointer events itself.
 */
const OWNS_ITS_CLICK = 'a, button, input, textarea, select, label, [role="button"], [data-keeps-click]'

/**
 * The card-surface click, as a hook, so the one card that is not built on `Card` behaves
 * identically to the three that are.
 *
 * LearningCard draws its own dark `<section>` (deep ground, `shadow-deep`) rather than taking the
 * white primitive, so without this the owner would get three cards that navigate and a fourth
 * that looks the same and does nothing — which is worse than none of them navigating.
 *
 * Returns undefined when `to` is undefined, so it can be spread unconditionally.
 */
export function useCardSurfaceLink(to?: string) {
  const navigate = useNavigate()
  if (!to) return undefined
  return (event: MouseEvent<HTMLElement>) => {
    if ((event.target as HTMLElement).closest(OWNS_ITS_CLICK)) return
    if ((window.getSelection()?.toString().length ?? 0) > 0) return
    navigate(to)
  }
}

/**
 * The context-card primitive, README §1: white, radius 16, 1px `line`,
 * `padding:18px 20px`, `shadow-card`. Its header row is an 18px domain-hue
 * icon, a 600 12.5px `ink-2` title, and a right-aligned 500 10.5px `faint`
 * qualifier with 0.05em tracking (`NO DEADLINE · AMBIENT`).
 *
 * Extracted on the third use, not the first (the Rule of Three, blueprint/02) —
 * the Today screen has four of these side by side.
 */
export function Card({
  icon,
  title,
  qualifier,
  action,
  children,
  className,
  border = 'border-line',
  to,
  linkLabel,
  hue,
}: {
  /** An 18px glyph already wearing its hue class. */
  icon?: ReactNode
  title?: ReactNode
  /** Right-aligned, uppercase, deepest quiet. Mutually exclusive with `action`. */
  qualifier?: ReactNode
  action?: ReactNode
  children: ReactNode
  className?: string
  /**
   * The border colour, as its own prop rather than something a caller sneaks in through
   * `className`: two border utilities in one class string are resolved by stylesheet order, not
   * by which one was written last, so an override there works by luck.
   */
  border?: string
  /**
   * WHERE THE WHOLE CARD GOES WHEN CLICKED (owner, 2026-09-01). Optional: a card without it
   * behaves exactly as before.
   *
   * TWO TARGETS, DELIBERATELY, because they answer different needs. The TITLE becomes a real
   * `<Link>` — that is the keyboard and screen-reader route, and it is the only one that shows a
   * URL on hover. The card SURFACE gets a click handler, which is the mouse convenience the owner
   * asked for and cannot be a link without wrapping every card in an anchor (illegal: these cards
   * contain buttons and a text field, and an anchor may not contain either).
   *
   * THE SURFACE CLICK YIELDS TO ITS CHILDREN. The owner's own caveat was the Reminders composer —
   * "which should just let you add a reminder" — so a click that started on anything owning its
   * own click is left alone, as is a click that ends a text selection. It is a convenience, never
   * an interception.
   */
  to?: string
  /** What the title link is called if `title` is not already plain words. */
  linkLabel?: string
  /**
   * The card's source hue, as a `var(--color-…)` reference — motion-brief.md §2's straight-rule
   * mechanic takes its colour from `--mo-rule`, which this sets inline so the hover state says
   * which domain the card belongs to before the click lands. Only meaningful alongside `to`.
   */
  hue?: string
}) {
  const onSurfaceClick = useCardSurfaceLink(to)

  return (
    <section
      onClick={onSurfaceClick}
      className={clsx(
        'flex min-h-0 flex-col rounded-context border bg-surface px-5 py-[18px] shadow-card',
        border,
        to && 'mo-rule cursor-pointer',
        className,
      )}
      style={to && hue ? ({ '--mo-rule': hue } as CSSProperties) : undefined}
    >
      {(icon || title || qualifier || action) && (
        <header className="flex flex-none items-center gap-[9px]">
          {icon}
          {title && (
            <h2 className="text-[12.5px] font-semibold leading-none text-ink-2">
              {to ? (
                <Link
                  to={to}
                  aria-label={linkLabel}
                  className="rounded-[3px] hover:underline"
                >
                  {title}
                </Link>
              ) : (
                title
              )}
            </h2>
          )}
          {qualifier && (
            <span className="ml-auto text-[10.5px] font-medium leading-none tracking-[0.05em] text-faint">
              {qualifier}
            </span>
          )}
          {action && <div className="ml-auto flex items-center">{action}</div>}
        </header>
      )}
      {children}
    </section>
  )
}

/**
 * A card whose endpoint does not exist yet has to SAY so, and an empty list is
 * a claim ("no classes today", "nothing due") that must never be made because
 * the data never arrived.
 */
export function EmptyLine({ children }: { children: ReactNode }) {
  return <p className="py-1 text-[12px] leading-[1.45] text-microcopy text-pretty">{children}</p>
}

/**
 * A loading skeleton that matches the final row geometry (README §Loading:
 * "Skeletons that match the final row geometry. Never a spinner over
 * already-readable content").
 */
export function SkeletonRows({ rows = 3, className }: { rows?: number; className?: string }) {
  return (
    <div className={clsx('flex flex-col gap-[10px]', className)} aria-hidden>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex items-center gap-3">
          <div className="h-[9px] w-[34px] flex-none rounded-full bg-fill" />
          <div className="h-[9px] flex-1 rounded-full bg-line-3" />
        </div>
      ))}
    </div>
  )
}
